/**
 * Full site footer for the ATSA section. Styled to match the marketing footer
 * (components/footer.tsx) and gives a visitor a way to reach BOTH the rest of
 * the ATSA practice area AND the wider modernatc.com site. Uses plain anchors
 * (full navigation, and cross-section links don't need typed-route casts).
 */
export function AtsaFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] py-14">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div>
            <span className="text-callsign text-sm font-semibold uppercase tracking-[0.08em] text-[var(--color-ink)]">
              MODERN<span className="text-[var(--color-amber)]">ATC</span>
            </span>
            <p className="mt-4 max-w-xs text-sm text-[var(--color-ink-dim)]">
              Independent, evidence-based ATSA practice and air traffic control
              career guidance. Original code, passages, and graphics.
            </p>
          </div>

          <FooterCol
            title="ATSA Practice"
            links={[
              { href: "/atsa", label: "All modules" },
              { href: "/atsa/full-mock", label: "Full mock" },
              { href: "/atsa/guides", label: "Study guides" },
              { href: "/atsa/progress", label: "Progress" },
              { href: "/atsa/why", label: "Why this prep" },
            ]}
          />

          <FooterCol
            title="Careers"
            links={[
              { href: "/atsa/careers", label: "ATC careers" },
              { href: "/atsa/careers/how-to-apply", label: "How to apply" },
              { href: "/atsa/careers/score-bands", label: "Score bands" },
            ]}
          />

          <FooterCol
            title="ModernATC"
            links={[
              { href: "/", label: "Home" },
              { href: "/blog", label: "Blog" },
              { href: "/pricing", label: "Pricing" },
              { href: "/about", label: "About" },
              { href: "/institutions", label: "Institutions" },
              { href: "/tools", label: "Free tools" },
            ]}
          />

          <FooterCol
            title="Support & legal"
            links={[
              { href: "/atsa/support", label: "Support" },
              { href: "mailto:hello@modernatc.com", label: "Contact" },
              { href: "/legal/terms", label: "Terms" },
              { href: "/legal/privacy", label: "Privacy" },
            ]}
          />
        </div>

        <div className="mt-12 h-px bg-[var(--color-panel-edge)]" />

        <div className="mt-6 flex flex-col items-start justify-between gap-4 text-xs text-[var(--color-ink-muted)] sm:flex-row sm:items-center">
          <p>
            <span className="text-callsign text-[10px] uppercase tracking-[0.08em]">
              {year}
            </span>{" "}
            ModernATC. All rights reserved.
          </p>
          <p className="max-w-2xl text-pretty leading-relaxed sm:text-right">
            Clean-room practice software. Not affiliated with, endorsed by, or
            sponsored by the FAA or Pearson VUE.
          </p>
        </div>
      </div>
    </footer>
  );
}

interface FooterColProps {
  title: string;
  links: { href: string; label: string }[];
}

function FooterCol({ title, links }: FooterColProps) {
  return (
    <div>
      <h3 className="text-callsign text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-muted)]">
        {title}
      </h3>
      <ul className="mt-4 space-y-2 text-sm text-[var(--color-ink-dim)]">
        {links.map((link) => (
          <li key={link.href}>
            <a href={link.href} className="hover:text-[var(--color-ink)]">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
